import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  InputBase,
  Badge,
  Box,
  Menu,
  MenuItem,
  Button,
  Container,
  Avatar,
  Tooltip,
  Grid,
} from '@mui/material';
import { ListItemIcon, ListItemText } from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import StoreIcon from "@mui/icons-material/Store";
import InfoIcon from "@mui/icons-material/Info";
import ContactMailIcon from "@mui/icons-material/ContactMail";
import { styled, alpha } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import AccountCircle from '@mui/icons-material/AccountCircle';
import ShoppingCartCheckoutIcon from "@mui/icons-material/ShoppingCartCheckout";
import FavoriteIcon from '@mui/icons-material/Favorite';
import { MdOutlineShoppingBag } from "react-icons/md";
import LogoutIcon from '@mui/icons-material/Logout';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { useDispatch, useSelector } from 'react-redux';
import logo from '../images/shaka-removebg-preview.png';
import { logout, syncLocalCart } from '../features/user/userSlice';

const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha('#000', 0.15),
  '&:hover': {
    backgroundColor: alpha('#000', 0.25),
  },
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    width: 'auto',
    minWidth: '200px',
  },
  [theme.breakpoints.up('md')]: {
    minWidth: '250px',
  },
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  position: 'absolute',
  left: theme.spacing(1),
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: '#ffffffff',
  '& input::placeholder': {
    color: '#ffffffff',
    opacity: 0.8,
  },
  paddingLeft: `calc(1em + ${theme.spacing(4)})`,
  paddingRight: theme.spacing(2),
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    width: '20ch',
    '&:focus': {
      width: '25ch',
    },
  },
}));

const NavButton = styled(Button)(({ theme }) => ({
  color: '#fff',
  '&.active': {
    fontWeight: 'bold',
    borderBottom: `2px solid #fff`,
    backgroundColor: alpha('#ffffffff', 0.1),
  },
}));

const Header = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [anchorElNav, setAnchorElNav] = useState(null);
  const [anchorElUser, setAnchorElUser] = useState(null);
  const [anchorElSmall, setAnchorElSmall] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { isAuthenticated, user, cartProducts } = useSelector((state) => state.auth);

  useEffect(() => {
    setSearchTerm('');
  }, [location.pathname]);

  useEffect(() => {
    if (!isAuthenticated) {
      const localCart = JSON.parse(localStorage.getItem('cart')) || [];
      dispatch(syncLocalCart(localCart));
    }
  }, [isAuthenticated, dispatch]);

  const cartItemCount = cartProducts?.length || 0;

  const handleSearch = () => {
    if (searchTerm.trim()) {
      navigate(`/product?search=${encodeURIComponent(searchTerm)}`);
      setSearchTerm('');
    }
  };

  const handleUserMenuOpen = (event) => setAnchorElUser(event.currentTarget);
  const handleUserMenuClose = () => setAnchorElUser(null);
  const handleSmallToggleOpen = (event) => setAnchorElSmall(event.currentTarget);
  const handleSmallToggleClose = () => setAnchorElSmall(null);

  const handleLogout = () => {
    localStorage.removeItem('customer');
    localStorage.removeItem('cart');
    dispatch(logout());
    navigate('/');
  };

  const getProfilePath = () => (user?._id ? `/profile/${user._id}` : '/login');

  const handleNav = (path) => {
    navigate(path);
  };

  const handleSmallNav = (path) => {
    handleSmallToggleClose();
    navigate(path);
  };

  const handleIconNav = (path) => {
    navigate(path);
  };

  const handleUserNav = (path) => {
    handleUserMenuClose();
    navigate(path);
  };

  return (
    <AppBar position="sticky" sx={{ bgcolor: '#053f5c', color: 'white', zIndex: 1000 }}>
      <Container maxWidth="xl">
        <Toolbar
          sx={{
            py: { xs: 1, sm: 1.5 },
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
            justifyContent: 'space-between',
          }}
          disableGutters
        >
          {/* Logo */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
              cursor: 'pointer',
            }}
            onClick={() => handleNav('/')}
          >
            <img
              src={logo}
              alt="Logo"
              style={{
                width: 50,
                height: 50,
                borderRadius: '50%',
                marginRight: 8,
              }}
            />
            <Typography
              variant="h5"
              noWrap
              sx={{
                color: '#ff6701',
                fontWeight: 600,
                fontFamily: 'inherit',
                fontSize: '1.5rem',
                '@media (min-width:600px)': { fontSize: '1.8rem' },
                '@media (min-width:900px)': { fontSize: '2rem' },
              }}
            >
              Shakambari
            </Typography>
          </Box>

          {/* Toggle icon - small screens */}
          <IconButton
            size="large"
            sx={{ color: '#fff', display: { xs: 'flex', md: 'none' } }}
            onClick={handleSmallToggleOpen}
          >
            <MenuIcon />
          </IconButton>

          {/* Nav links - large screens */}
          <Box
            sx={{
              display: { xs: 'none', lg: 'block', md: 'flex' },
              gap: 1,
              flexGrow: 1,
              justifyContent: 'center',
              ml: '70px',
            }}
          >
            <NavButton onClick={() => handleNav('/')}>Home</NavButton>
            <NavButton onClick={() => handleNav('/product')}>Shop</NavButton>
            <NavButton onClick={() => handleNav('/about')}>About</NavButton>
            <NavButton onClick={() => handleNav('/contact')}>Contact</NavButton>
          </Box>

          {/* Search + icons */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              maxWidth: { sm: 300, md: 400 },
              order: { xs: 3, sm: 2 },
            }}
          >
            {/* Search */}
            <Grid container alignItems="center">
              {/* Search Bar */}
              <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                <Box sx={{ flexGrow: 1 }}>
                  <Search sx={{ width: "100%" }}>
                    <SearchIconWrapper>
                      <SearchIcon />
                    </SearchIconWrapper>
                    <StyledInputBase
                      placeholder="Search…"
                      inputProps={{ "aria-label": "search" }}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                    />
                  </Search>
                </Box>

                {/* Icons aligned right */}
                <Box
                  sx={{
                    display: { xs: "flex", md: "none" }, // show only on xs/sm
                    alignItems: "center",
                    gap: 2,
                    ml: "auto",
                  }}
                >
                  {/* Wishlist */}
                  <IconButton onClick={() => handleIconNav('/wishlist')} sx={{ p: 1 }}>
                    <FavoriteIcon sx={{ fontSize: 28, color: "red" }} />
                  </IconButton>

                  {/* Cart */}
                  <IconButton onClick={() => handleIconNav('/cart')} sx={{ p: 1, color: '#fff' }}>
                    <Badge badgeContent={cartItemCount} color="error">
                      <MdOutlineShoppingBag />
                    </Badge>
                  </IconButton>

                  {/* Profile / Login */}
                  {isAuthenticated && user?.email ? (
                    <IconButton onClick={handleUserMenuOpen} sx={{ p: 0 }}>
                      <Avatar
                        sx={{
                          width: 40,
                          height: 40,
                          bgcolor: "primary.main",
                          color: "#fff",
                          fontSize: "1rem",
                        }}
                      >
                        {user.email.charAt(0).toUpperCase()}
                      </Avatar>
                    </IconButton>
                  ) : (
                    <IconButton onClick={() => { handleSmallToggleClose(); handleIconNav('/login'); }} sx={{ p: 0 }}>
                      <AccountCircle sx={{ fontSize: 32, color: "#fff" }} />
                    </IconButton>
                  )}
                </Box>

              </Box>
            </Grid>


            {/* Small screen nav menu */}
            <Box sx={{ display: { xs: "block", md: "none" }, ml: 1 }}>
              <Menu
                anchorEl={anchorElSmall}
                open={Boolean(anchorElSmall)}
                onClose={handleSmallToggleClose}
                PaperProps={{
                  sx: {
                    width: 180, // small modal width
                    maxHeight: 300, // control height
                  },
                }}
              >
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <MenuItem
                    onClick={() => handleSmallNav('/')}
                    sx={{ color: "#000" }}
                  >
                    <ListItemIcon>
                      <HomeIcon fontSize="small" sx={{ color: "#000" }} />
                    </ListItemIcon>
                    <ListItemText primary="Home" />
                  </MenuItem>

                  <MenuItem
                    onClick={() => handleSmallNav('/product')}
                    sx={{ color: "#000" }}
                  >
                    <ListItemIcon>
                      <StoreIcon fontSize="small" sx={{ color: "#000" }} />
                    </ListItemIcon>
                    <ListItemText primary="Shop" />
                  </MenuItem>

                  <MenuItem
                    onClick={() => handleSmallNav('/about')}
                    sx={{ color: "#000" }}
                  >
                    <ListItemIcon>
                      <InfoIcon fontSize="small" sx={{ color: "#000" }} />
                    </ListItemIcon>
                    <ListItemText primary="About" />
                  </MenuItem>

                  <MenuItem
                    onClick={() => handleSmallNav('/contact')}
                    sx={{ color: "#000" }}
                  >
                    <ListItemIcon>
                      <ContactMailIcon fontSize="small" sx={{ color: "#000" }} />
                    </ListItemIcon>
                    <ListItemText primary="Contact" />
                  </MenuItem>
                </Box>
              </Menu>
            </Box>
          </Box>

          {/* Icons - large only */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1, order: 2 }}>
            <IconButton onClick={() => handleIconNav('/wishlist')} sx={{ color: 'red' }} size="large">
              <FavoriteIcon />
            </IconButton>

            <IconButton onClick={() => handleIconNav('/cart')} sx={{ color: '#fff' }} size="large">
              <Badge badgeContent={cartItemCount} color="error">
                <MdOutlineShoppingBag sx={{ fontSize: 28, color: "#fff" }} />
              </Badge>
            </IconButton>
            
            {isAuthenticated && user?.email ? (
              <Tooltip title={user.email}>
                <IconButton onClick={handleUserMenuOpen} sx={{ color: '#fff' }} size="large">
                  <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main', color: '#fff' }} alt={user.email}>
                    {user.email.charAt(0).toUpperCase()}
                  </Avatar>
                </IconButton>
              </Tooltip>
            ) : (
              <IconButton onClick={() => handleIconNav('/login')} sx={{ color: '#fff' }} size="large">
                <AccountCircle />
              </IconButton>
            )}
            
          </Box>

          {/* Shared User Menu */}
          {isAuthenticated && (
            <Menu anchorEl={anchorElUser} open={Boolean(anchorElUser)} onClose={handleUserMenuClose}>
              <MenuItem onClick={() => handleUserNav(getProfilePath())} sx={{ color: '#000' }}>
                <AccountCircleIcon sx={{ mr: 1 }} />Profile
              </MenuItem>
              <MenuItem onClick={() => handleUserNav('/orders')} sx={{ color: '#000' }}>
                <ShoppingCartCheckoutIcon sx={{ mr: 1 }} />My Orders
              </MenuItem>
              <MenuItem onClick={handleLogout} sx={{ color: '#000' }}>
                <LogoutIcon sx={{ mr: 1 }} />Logout
              </MenuItem>
            </Menu>
          )}
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default Header;